import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Normalise a string for fuzzy text search: lower-cased, accent-stripped,
 * whitespace-collapsed. So "João" and "joao" — and "JOÃO   da   SILVA" —
 * all compare equal. Returns "" for null/undefined.
 */
export function normalizeForSearch(value: unknown): string {
  if (value == null) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining diacritical marks
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Token-based fuzzy match: every whitespace-separated token of `query` must
 * appear (as a substring) somewhere in `haystack` (the joined, normalised
 * searchable fields). So "joao silva" matches "JOÃO MANUEL DA SILVA" even
 * though the tokens aren't adjacent, and order doesn't matter.
 */
export function matchesSearch(query: string, ...fields: unknown[]): boolean {
  const q = normalizeForSearch(query);
  if (!q) return true;
  const haystack = fields.map(normalizeForSearch).filter(Boolean).join(" ");
  return q.split(" ").every((tok) => haystack.includes(tok));
}

/**
 * Fisher-Yates shuffle — returns a new shuffled array, does not mutate input.
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
